import React, { useCallback, useEffect, useRef, useState } from "react";
import { Radio, X, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import { CallOverlay } from "@/components/AudioCall";

/**
 * Global listener that polls `/api/voice/broadcast/status` every ~5s
 * whenever a member is signed in. When a live broadcast is detected,
 * shows a small floating banner ("🔴 Live — Tap to listen"). The
 * admin who started the broadcast doesn't see the banner: they
 * already have full controls in their own live-overlay.
 *
 * On tap → POST `/voice/broadcast/join` to get a listener-only token,
 * then hands it to `<CallOverlay/>` for the actual audio session.
 *
 * The banner is dismissible per-broadcast (localStorage). Once the
 * admin ends the broadcast the ID rotates so a fresh banner comes
 * back next time.
 */
const POLL_MS = 5000;
const DISMISS_KEY = "ch_broadcast_dismissed_id";

export default function BroadcastListener() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [status, setStatus] = useState({ active: false, broadcast: null });
  const [joined, setJoined] = useState(null); // token payload after Join
  const [busy, setBusy] = useState(false);
  const [dismissedId, setDismissedId] = useState(() => {
    if (typeof window === "undefined") return null;
    try { return window.localStorage.getItem(DISMISS_KEY); } catch { return null; }
  });
  const timerRef = useRef(null);

  const poll = useCallback(async () => {
    if (!user) return;
    try {
      const r = await api.get("/voice/broadcast/status");
      setStatus(r.data || { active: false, broadcast: null });
      // Rotate the dismiss key: if the current active broadcast id
      // differs from what we last dismissed, clear the flag so a fresh
      // banner appears for the new session.
      const activeId = r.data?.broadcast?.id;
      if (activeId && dismissedId && activeId !== dismissedId) {
        setDismissedId(null);
        try { window.localStorage.removeItem(DISMISS_KEY); } catch { /* ignore */ }
      }
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.debug("voice/broadcast poll error", err?.message);
      }
    }
  }, [user, dismissedId]);

  useEffect(() => {
    if (!user) {
      setStatus({ active: false, broadcast: null });
      setJoined(null);
      return;
    }
    let stopped = false;
    const tick = () => {
      if (stopped) return;
      if (document.visibilityState === "visible") poll();
      timerRef.current = window.setTimeout(tick, POLL_MS);
    };
    tick();
    const onVis = () => { if (document.visibilityState === "visible") poll(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stopped = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [user, poll]);

  const join = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post("/voice/broadcast/join");
      setJoined(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("voiceStartError"));
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    const id = status.broadcast?.id;
    if (!id) return;
    setDismissedId(id);
    try { window.localStorage.setItem(DISMISS_KEY, id); } catch { /* ignore */ }
  };

  const leave = () => {
    setJoined(null);
    // After leaving, treat the current broadcast as "dismissed" so we
    // don't nag the same listener with the banner immediately.
    const id = status.broadcast?.id;
    if (id) {
      setDismissedId(id);
      try { window.localStorage.setItem(DISMISS_KEY, id); } catch { /* ignore */ }
    }
  };

  // Auto-join when the user arrives here from tapping the "Listen"
  // action on a broadcast push. URL query is `?broadcast=<id>&auto=1`.
  const autoJoinedRef = useRef(null);
  useEffect(() => {
    if (!status.active || joined || busy) return;
    let params;
    try { params = new URLSearchParams(window.location.search); }
    catch (_) { return; }
    if (params.get("auto") !== "1") return;
    const wanted = params.get("broadcast");
    if (!wanted || wanted !== status.broadcast?.id) return;
    if (autoJoinedRef.current === wanted) return;
    autoJoinedRef.current = wanted;
    try {
      const clean = new URL(window.location.href);
      clean.searchParams.delete("auto");
      clean.searchParams.delete("broadcast");
      window.history.replaceState({}, "", clean.toString());
    } catch (_) { /* ignore */ }
    join();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, joined, busy]);

  // If they've already joined, hand control to CallOverlay. When it
  // reports disconnect (admin ended the broadcast, network failed,
  // user tapped Hang up), we reset and let the banner reappear on the
  // next active session.
  if (joined) {
    return (
      <CallOverlay
        state="connecting"
        tokenPayload={joined}
        kind="church_broadcast"
        onConnected={() => {}}
        onDisconnected={leave}
        onError={() => leave()}
      />
    );
  }

  if (!status.active) return null;
  // Admin who started it doesn't need this banner — their ChatConversation
  // header already has "End broadcast" + they saw the CallOverlay when
  // they started.
  if (user?.id === status.broadcast?.started_by) return null;
  // Dismissed by this listener for this session.
  if (dismissedId && dismissedId === status.broadcast?.id) return null;

  const from = status.broadcast?.started_by_display || t("broadcastLive");

  return (
    <div
      className="fixed inset-x-0 z-[85] px-3 pointer-events-none"
      style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 5rem)" }}
      data-testid="broadcast-banner"
    >
      <div className="mx-auto max-w-md pointer-events-auto rounded-2xl bg-stone-900 text-white shadow-2xl border border-white/10 overflow-hidden">
        <div className="flex items-center gap-3 px-3 py-2.5">
          <div className="w-10 h-10 rounded-full bg-red-500/20 grid place-items-center shrink-0 relative">
            <Radio className="w-5 h-5 text-red-400" />
            <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.15em] text-red-300 font-semibold">
              {t("broadcastLive")}
            </div>
            <div className="text-sm font-semibold truncate" data-testid="broadcast-banner-title">
              {t("broadcastFromLower")} {from}
            </div>
          </div>
          <button
            type="button"
            onClick={join}
            disabled={busy}
            data-testid="broadcast-join-btn"
            className="h-9 px-3 rounded-full bg-white text-stone-900 text-xs font-semibold hover:bg-stone-200 transition disabled:opacity-60 inline-flex items-center gap-1.5"
          >
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {t("broadcastListenNow")}
          </button>
          <button
            type="button"
            onClick={dismiss}
            data-testid="broadcast-dismiss-btn"
            aria-label="Dismiss"
            className="w-8 h-8 grid place-items-center rounded-full text-white/50 hover:text-white hover:bg-white/10 shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
